import { useCallback, useMemo } from 'react';
import {
  ContactDto,
  useInfiniteClientContacts,
  buildSorter,
  buildQueryFilters,
} from '@sib/api';
import { DEFAULT_PAGE } from '@sib/api/constants';
import { ComboboxTagOptionProps } from '@sib/ui/types';
import { useAutocompleteProps } from '@sib/utils';

export interface UseClientContactsAutocompleteProps {
  organizationUuid?: string | null;
  search?: string;
  useEmail?: boolean;
}

export const useClientContactsAutocomplete = ({
  organizationUuid,
  search = '',
  useEmail = false,
}: UseClientContactsAutocompleteProps) => {
  const params = useInfiniteClientContacts({
    organizationId: organizationUuid as string,
    options: {
      enabled: !!organizationUuid,
    },
    payload: {
      page: DEFAULT_PAGE,
      size: 5,
      filter: buildQueryFilters([
        {
          field: 'name',
          operator: 'contains',
          value: search,
        },
      ]),
      sort: [
        buildSorter({
          field: 'name',
          order: 'asc',
        }),
      ],
    },
  });

  const optionsMapper = useCallback(
    (contact: ContactDto): ComboboxTagOptionProps => {
      const value = useEmail ? contact?.email || '' : contact.id;

      return {
        label: contact.name ?? '',
        value,
        secondaryValue: useEmail ? contact.id : contact.email,
        color: 'var(--color-secondary)',
      };
    },
    [useEmail]
  );

  const autocompleteResult = useAutocompleteProps({
    params,
    filter: search,
    optionsMapper,
  });

  const totalItems = params.data?.pages?.[0]?.totalItems ?? 0;

  return useMemo(
    () => ({
      ...autocompleteResult,
      totalItems,
    }),
    [autocompleteResult, totalItems]
  );
};
